"use client";

import { Flower2, Leaf, Sun, Moon, Sparkles, Coffee, Tag, Users, Calendar, Bath, BookOpen, Heart, Star, Gift, Wind } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function WhyPrioritizeYourWellBeing() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: imageRef,
    isVisible: imageVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: contentRef,
    isVisible: contentVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const categories = [{
    title: "Clinical Approach",
    benefits: [{
      icon: BookOpen,
      name: "Evidence-Based Care",
      description: "Utilizing proven therapeutic techniques tailored to your unique healing journey."
    }, {
      icon: Heart,
      name: "Safe & Non-Judgmental",
      description: "A confidential space where you are heard, respected, and fully supported."
    }, {
      icon: Wind,
      name: "Flexible Scheduling",
      description: "Day, evening, and telehealth sessions designed to fit your busy life."
    }, {
      icon: Sparkles,
      name: "Multilingual Support",
      description: "Professional counseling services available in English, Spanish, and Portuguese."
    }]
  }, {
    title: "Specialized Focus",
    benefits: [{
      icon: Coffee,
      name: "Individual Therapy",
      description: "Personalized guidance to navigate depression, anxiety, and trauma."
    }, {
      icon: Tag,
      name: "Relationship Healing",
      description: "Expert support for couples and families to rebuild connection and trust."
    }, {
      icon: Leaf,
      name: "Neurodivergent Care",
      description: "Compassionate strategies for managing unique cognitive and emotional challenges."
    }, {
      icon: Moon,
      name: "Bipolar Management",
      description: "Structured support to help you find the balance you are looking for."
    }]
  }, {
    title: "Getting Started",
    benefits: [{
      icon: Gift,
      name: "Free Consultations",
      description: "Take the first step with a complimentary initial conversation."
    }, {
      icon: Calendar,
      name: "10+ Years Experience",
      description: "Benefit from over a decade of dedicated counseling expertise."
    }, {
      icon: Users,
      name: "Personalized Growth",
      description: "We work together to help you become the best version of yourself."
    }, {
      icon: Star,
      name: "Direct Communication",
      description: "Reach out at 407-376-2963 or tanyarp@hotmail.com to begin."
    }]
  }];
  return <section data-section-id="158" id="why-prioritize-your-well-being" className="bg-background py-[140px] px-6 lg:px-12 text-foreground">
      <div className="max-w-7xl mx-auto">
        {}
        <div ref={headerRef} className={`text-center mb-20 transition-all duration-1000 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <p className="font-mono text-xs tracking-[0.3em] uppercase text-primary mb-6">
            Our Commitment
          </p>
          <h2 className="font-serif font-default text-4xl md:text-5xl lg:text-[52px] leading-tight text-foreground">
            Rebuild your <em className="italic text-accent-foreground">life.</em>
          </h2>
          <div className="w-12 h-px bg-border mx-auto mt-8" />
        </div>

        {}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          {}
          <div ref={imageRef} className={`lg:col-span-4 transition-all duration-1000 ease-out ${imageVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <div className="lg:sticky lg:top-24 overflow-hidden rounded-2xl shadow-xl">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1699238/img/therapy_session_calm_space.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1699238/img/therapy_session_calm_space.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1699238/img/therapy_session_calm_space.png-wvc-sizes" alt="A calm, professional therapy office space with soft lighting and comfortable seating" loading="lazy" decoding="async" className="w-full h-[400px] lg:h-[720px] object-cover" />
            </div>
          </div>

          {}
          <div ref={contentRef} className={`lg:col-span-8 transition-all duration-1000 delay-150 ease-out ${contentVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <div className="space-y-14">
              {categories.map((category, catIndex) => <div data-index={catIndex} key={catIndex}>
                  {catIndex > 0 && <div className="w-full h-px bg-border mb-14" aria-hidden="true" />}

                  <h3 className="font-serif font-default text-2xl text-foreground mb-8 italic">
                    {category.title}
                  </h3>

                  <ul className="space-y-6">
                    {category.benefits.map((benefit, i) => {
                  const Icon = benefit.icon;
                  return <li data-index={i} key={i} className="group flex items-start gap-5 cursor-default">
                          <div className="shrink-0 mt-1">
                            <Icon className="w-5 h-5 text-accent-foreground transition-all duration-500 ease-out group-hover:scale-125" strokeWidth={1.25} aria-hidden="true" />
                          </div>
                          <div className="flex-1">
                            <h4 className="font-serif font-default text-lg text-foreground transition-colors duration-500 group-hover:text-accent-foreground">
                              {benefit.name}
                            </h4>
                            <p className="font-sans text-sm text-muted-foreground mt-1 leading-relaxed">
                              {benefit.description}
                            </p>
                          </div>
                        </li>;
                })}
                  </ul>
                </div>)}
            </div>

            {}
            <div className="mt-16 pt-10 border-t border-border">
              <p className="font-serif font-default text-xl italic text-muted-foreground leading-relaxed">
                "You deserve to be heard. Your inner voice will thank you later for prioritizing YOU."
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>;
}